/**
 * Ce que la trousse contient. PUR : une liste d'entrées, l'ordre, et celle qu'on met en avant.
 *
 * La trousse remplace la barre d'icônes de l'exercice « sur la feuille » : on y trouve les outils de
 * la feuille (tableau à dresser, arbre, avancement, corrigé) ET les icônes du player (Cours,
 * Calculatrice, Boîte à outils, Défi, Me filmer) et les repères de l'énoncé (figure, tableau).
 */
import type { LucideIcon } from 'lucide-react';

export type TrousseGroup =
  /** ce que la feuille attend ou propose : tableau, arbre, avancement, corrigé */
  | 'sheet'
  /** aide pendant l'exercice : cours, calculatrice, boîte à outils */
  | 'help'
  /** se repérer dans l'énoncé : figure, tableau, graphique */
  | 'anchor'
  /** partager : défi, se filmer */
  | 'share';

export interface TrousseItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** un signe dessiné à la place de l'icône (⊞ tableau de signes, ⑂ arbre…) */
  mark?: string;
  group: TrousseGroup;
  /** l'outil que l'exercice attend : en or, et la trousse appelle */
  highlight?: boolean;
  /** vrai quand l'outil est déjà en cours (caméra allumée) */
  active?: boolean;
  onPick: () => void;
}

const ORDER: TrousseGroup[] = ['sheet', 'help', 'anchor', 'share'];

/** L'ordre d'affichage : ce que la feuille attend d'abord, le partage en dernier. */
export function sortTrousseItems(items: TrousseItem[]): TrousseItem[] {
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const g = ORDER.indexOf(a.it.group) - ORDER.indexOf(b.it.group);
      if (g) return g;
      // à groupe égal, l'outil attendu passe devant
      const h = Number(!!b.it.highlight) - Number(!!a.it.highlight);
      return h || a.i - b.i;
    })
    .map(({ it }) => it);
}

/** La trousse appelle (pastille dorée) quand l'exercice attend un de ses outils. */
export function trousseCalls(items: TrousseItem[]): boolean {
  return items.some((it) => it.highlight);
}

/** Ce que le lecteur d'écran annonce : le nom de la trousse, et ce qu'elle réclame. */
export function trousseAria(name: string, items: TrousseItem[]): string {
  const call = items.find((it) => it.highlight);
  return call ? `Ma trousse — ${name} : ${call.label} à faire` : `Ma trousse — ${name}`;
}
